import React, { useEffect, useState } from "react";
import { Modal, Table, message, Space, Button, Tooltip } from "antd";
import {
    AppstoreOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    OrderedListOutlined
} from "@ant-design/icons";
import axios from "axios";
import { getConfig } from "../../../../../config";
import TransferProductEditWizard from "../transferProjectProductViews/TransferProductEditWizard";
import "./TransferProductMain.css";
const { API_BASE_URL } = getConfig();

interface TransferProductMainProps {
    visible: boolean;
    onClose: () => void;
    onCompletion: () => void;
    selectedProject: string;
}

const TransferProductMain: React.FC<TransferProductMainProps> = ({ visible, onClose, onCompletion, selectedProject }) => {
    const [data, setData] = useState<ProductData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditWizardVisible, setIsEditWizardVisible] = useState(false);
    const [selectedProductCode, setSelectedProductCode] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(null);
    const [selectedTasks, setSelectedTasks] = useState<LegacyTask[]>([]);
    const [isTaskSelectionVisible, setIsTaskSelectionVisible] = useState(false);

    useEffect(() => {
        if (visible && selectedProject) {
            fetchData(selectedProject);
        }
    }, [visible, selectedProject]);

    useEffect(() => {
        console.log("Task Selection Modal State:", isTaskSelectionVisible);
    }, [isTaskSelectionVisible]);


    const fetchData = async (projectNo: string) => {
        setIsLoading(true);
        try {
            const response = await axios.get<ProductData[]>(
                `${API_BASE_URL}/getLegacyProductData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}`
            );
            setData(response.data);
        } catch (error) {
            console.error("Error fetching product data:", error);
            message.error("Fehler beim Laden der Produktdaten.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (productCode: string, record: ProductData) => {
        console.log("Edit clicked for:", record); // Debugging
        if (record.allowAllTasks) {
            console.log("Opening Task Selection Modal"); // Debugging
            setSelectedProduct(record);
            setIsTaskSelectionVisible(true);
        } else {
            setSelectedProductCode(productCode);
            setSelectedProduct(record);
            setSelectedTasks(record.tasks);
            setIsEditWizardVisible(true);
        }
    };

    const handleTaskSelection = (task: LegacyTask) => {
        if (task.code === "SYSTEM_PLANNING_OPEN" && selectedProduct) {
            Modal.confirm({
                width: 600,
                title: "Ist die Systemplanung wirklich noch offen?",
                content: (
                    <ul>
                        <li>{selectedProduct.name} (#{selectedProduct.code})</li>
                    </ul>
                ),
                okText: "Ja",
                cancelText: "Abbrechen",
                onOk: async () => {
                    try {
                        await axios.get(`${API_BASE_URL}/saveOpenNetworkplanStatus`, {
                            params: {
                                projectNo: selectedProject,
                                productCode: selectedProduct.code,
                                taskType: task.code,
                            }
                        });
                        message.success("Status wurde erfolgreich gesetzt.");
                        fetchData(selectedProject);
                    } catch (error) {
                        message.error("Fehler beim Buchen des Status.");
                    } finally {
                        setIsTaskSelectionVisible(false);
                    }
                },
            });
            return;
        }
        if (task.code === "SYSTEM_PLANNING_GOODS_RECEIVING" && selectedProduct) {
            Modal.confirm({
                width: 600,
                title: "Willst Du den Wareneingang Systemplanung buchen?",
                content: (
                    <ul>
                        <li>{selectedProduct.name} (#{selectedProduct.code})</li>
                    </ul>
                ),
                okText: "Ja",
                cancelText: "Abbrechen",
                onOk: async () => {
                    try {
                        await axios.get(`${API_BASE_URL}/saveTransferProjectJobGoodsReceiving`, {
                            params: {
                                projectNo: selectedProject,
                                productCode: selectedProduct.code,
                            }
                        });
                        message.success("Wareneingang erfolgreich gebucht.");
                        fetchData(selectedProject);
                    } catch (error) {
                        message.error("Fehler beim Buchen des Wareneingangs.");
                    } finally {
                        setIsTaskSelectionVisible(false);
                    }
                },
            });
            return;
        }
        let tasksToPass: LegacyTask[] = [task];
        if (task.description === "Systemfreigabe Dokumentieren" && selectedProduct) {
            const optionalTasks = selectedProduct.tasks.filter(t =>
                t.description === "Farb Reservation" || t.description === "Material Reservation"
            );
            tasksToPass = [...tasksToPass, ...optionalTasks];
        }
        setSelectedProduct((prev) => prev || selectedProduct);
        setSelectedTasks(tasksToPass);
        setIsTaskSelectionVisible(false);
        setTimeout(() => {
            setIsEditWizardVisible(true);
        }, 200);
    };

    const handleWizardClose = () => {
        setIsEditWizardVisible(false);
        setSelectedProductCode(null);
        setSelectedProduct(null)
        setSelectedTasks([]);
        fetchData(selectedProject); // Reload data after editing
    };

    const columns = [
        {
            title: "Status",
            dataIndex: "statusDone",
            key: "statusDone",
            className: "centered-column",
            width: 80,
            render: (statusDone: boolean) => (
                statusDone ? <CheckCircleOutlined style={{ display: "flex", justifyContent: "center", color: "green", fontSize: "18px" }} />
                    : <CloseCircleOutlined style={{ display: "flex", justifyContent: "center", color: "red", fontSize: "18px" }} />
            ),
        },
        {
            title: "Produkt",
            dataIndex: "code",
            key: "code",
            width: 120,
            sorter: (a: ProductData, b: ProductData) => a.code.localeCompare(b.code)
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            width: 200,
            sorter: (a: ProductData, b: ProductData) => a.name.localeCompare(b.name)
        },
        {
            title: "Status Min/Max",
            dataIndex: "statusMin", // You can use any key here; it's not used in render
            key: "status",
            width: 200,
            render: (_: string, record: ProductData) => (
                <span>{record.statusMin} / {record.statusMax}</span>
            ),
        },
        {
            title: "Optionen / Aktueller Status",
            dataIndex: "tasks",
            key: "taskType",
            width: 350,
            render: (tasks: LegacyTask[]) => (
                <div>
                    {tasks.map((task, index) => (
                        <div key={index}>
                            {task.description}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            title: "",
            dataIndex: "code",
            key: "edit",
            className: "righted-column",
            width: 100,
            render: (code: string, record: ProductData) => (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title={record.allowAllTasks ? "Option auswählen" : "Bearbeiten"}>
                        <Button
                            disabled={!record.editable}
                            icon={record.allowAllTasks ? <OrderedListOutlined /> : <EditOutlined />}
                            onClick={() => handleEdit(code, record)}
                            type="primary"
                        />
                    </Tooltip>
                </div>
            ),
        }
    ];

    return (
        <>
            <Modal
                title="Wähle einen Option"
                visible={isTaskSelectionVisible}
                onCancel={() => setIsTaskSelectionVisible(false)}
                footer={null}
            >
                {selectedProduct?.tasks
                .filter(task => task.description !== "Farb Reservation" && task.description !== "Material Reservation")
                .map((task) => (
                    <Button
                        key={task.code}
                        style={{ width: "100%", marginBottom: "8px" }}
                        onClick={() => handleTaskSelection(task)}
                    >
                        {task.description}
                    </Button>
                ))}
            </Modal>
            <Modal
                title={`Produkte im Projekt ${selectedProject}`}
                visible={visible}
                onCancel={onClose}
                footer={null}
                width="70vw"
                style={{top: "0.0vh"}}
                styles={{
                    body: {height: "85vh", overflowY: "auto", padding: "20px"},
                    content: {top: "2.5vh", height: "95vh"}
                }}
            >
                <div className="transfer-product-table">
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="code"
                        pagination={{pageSize: 100}}
                        loading={isLoading}
                        size="middle"
                        rowClassName={(record) => (record.statusDone ? "row-disabled" : "")}
                    />
                </div>
            </Modal>
            {selectedProduct && selectedTasks.length > 0 && (
                <TransferProductEditWizard
                    visible={isEditWizardVisible}
                    onClose={handleWizardClose}
                    selectedProduct={selectedProduct}
                    selectedProject={selectedProject}
                    tasks={selectedTasks}
                />
            )}
        </>
    );
};

export default TransferProductMain;